import React, { useRef, useState, useEffect } from "react";
import { Eraser, PenLine, Type } from "lucide-react";

const TYPED_FONTS = [
  { key: "cursive", label: "Signature", style: { fontFamily: "'Brush Script MT', cursive" } },
  { key: "serif", label: "Formal", style: { fontFamily: "Georgia, serif", fontStyle: "italic" } },
  { key: "mono", label: "Print", style: { fontFamily: "'JetBrains Mono', monospace" } },
];

// onChange(type, data)  where type is "draw" | "typed"
export default function SignaturePad({ onChange, defaultName = "" }) {
  const [mode, setMode] = useState("draw");
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [typed, setTyped] = useState(defaultName);
  const [font, setFont] = useState(TYPED_FONTS[0]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0F172A";
  }, [mode]);

  useEffect(() => {
    // Propagate the pre-filled name when switching to typed mode so an
    // unedited signature still registers with the parent.
    if (mode === "typed" && typed) onChange("typed", typed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasDrawn) onChange("draw", canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onChange("draw", "");
  };

  const changeTyped = (val, f) => {
    setTyped(val);
    if (f) setFont(f);
    onChange("typed", val);
  };

  return (
    <div data-testid="signature-pad" className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden">
      <div className="flex border-b border-[#E2E8F0]">
        <button
          type="button"
          data-testid="signature-mode-draw"
          onClick={() => setMode("draw")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-colors ${
            mode === "draw" ? "bg-[#EFF6FF] text-[#2563EB]" : "text-[#64748B] hover:bg-slate-50"
          }`}
        >
          <PenLine size={16} /> Draw
        </button>
        <button
          type="button"
          data-testid="signature-mode-type"
          onClick={() => setMode("typed")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-colors ${
            mode === "typed" ? "bg-[#EFF6FF] text-[#2563EB]" : "text-[#64748B] hover:bg-slate-50"
          }`}
        >
          <Type size={16} /> Type
        </button>
      </div>

      {mode === "draw" ? (
        <div className="relative">
          <canvas
            ref={canvasRef}
            data-testid="signing-canvas-pad"
            className="sig-canvas w-full h-40 block"
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
          />
          {!hasDrawn && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-[#94A3B8]">
              Sign here with your mouse or finger
            </span>
          )}
          <button
            type="button"
            data-testid="signing-clear-btn"
            onClick={clear}
            className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md bg-white border border-[#E2E8F0] px-2 py-1 text-xs font-medium text-[#64748B] hover:text-[#0F172A]"
          >
            <Eraser size={13} /> Clear
          </button>
        </div>
      ) : (
        <div className="p-4 space-y-3">
          <input
            data-testid="signature-typed-input"
            value={typed}
            onChange={(e) => changeTyped(e.target.value, font)}
            placeholder="Type your full name"
            className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm outline-none focus:border-[#2563EB]"
          />
          <div className="flex gap-2">
            {TYPED_FONTS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => changeTyped(typed, f)}
                className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
                  font.key === f.key ? "border-[#2563EB] text-[#2563EB] bg-[#EFF6FF]" : "border-[#E2E8F0] text-[#64748B]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="rounded-lg border border-dashed border-[#E2E8F0] h-20 flex items-center px-4">
            <span className="text-3xl text-[#0F172A]" style={font.style}>
              {typed || "Your signature"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
